import { useEffect, useState, useRef, useCallback } from "react";
import "./App.css";
import config from "./config";
import LandingScreen from "./components/LandingScreen";
import ScreenSizeError from "./components/ScreenSizeError";
import LoadingSpinner from "./components/LoadingSpinner";
import TermsAndConditions from "./components/TermsAndConditions";
import Consent from "./components/Consent";
import StartJourney from "./components/StartJourney";
import ScanDocumentScreenNew from "./components/ScanDocumentScreenNew";
import SelfieScreenTwo from "./components/SelfieScreenTwo";
import SelfieScreenOnce from "./components/SelfieScreenOnce";
import CompleteJourneyScreenNew from "./components/CompleteJourneyScreenNew";
import ProcessCompleteJourney from "./components/ProcessCompleteJourney";
import { GRE_PROGRAM_ID, TOEFL_PROGRAM_ID } from "./constants/Constants";

function App() {
  const [contextId, setContextId] = useState("");
  const [correlationId, setCorrelationId] = useState("");
  const [programId, setProgramId] = useState("");
  const [initStatus, setInitStatus] = useState("");
  const [docStatus, setDocStatus] = useState("");
  const [isWorkflowEnabled, setIsWorkflowEnabled] = useState(false);
  const [hasInitError, setHasInitError] = useState(false);
  const [isDocScanEnabled, setIsDocScanEnabled] = useState(false);
  const [, setIsDocScanComplete] = useState(false);
  const [isNextStepsEnabled, setIsNextStepsEnabled] = useState(false);
  const [docError, setDocError] = useState("");
  const [showDocSuccess, setShowDocSuccess] = useState(false);
  const [, setSelfieFile] = useState(null);
  const [selfieStatus, setSelfieStatus] = useState("");
  const [selfieError, setSelfieError] = useState("");
  const [showSelfieSuccess, setShowSelfieSuccess] = useState(false);
  const [isCompleteJourneyEnabled, setIsCompleteJourneyEnabled] =
    useState(false);
  const [showJourneyCompleteSuccess, setShowJourneyCompleteSuccess] =
    useState(false);
  const initStatusRef = useRef(null);
  const [initErrorStatus, setInitErrorStatus] = useState("");
  const [startIdvJourney, setStartIdvJourney] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [shouldAutoInit, setShouldAutoInit] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [journeySteps, setJourneySteps] = useState(1);
  const isErrorFromEventRef = useRef(false);
  const [screenErrorTitle, setScreenErrorTitile] = useState("");
  const [screenErrorMsg, setScreenErrorMsg] = useState("");

  // #region-Extract URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const contextIdParam = urlParams.get("context_id");
    const correlationIdParam = urlParams.get("correlation_id");
    const programId = urlParams.get("program_id") || TOEFL_PROGRAM_ID;
    sessionStorage.setItem("program_id", programId);
    const favicon = document.querySelector("link[rel='icon']");
    if (favicon) {
      if (programId == GRE_PROGRAM_ID) {
        favicon.href = "/gre-logo.svg";
      }
    }
    if (
      !contextIdParam ||
      !correlationIdParam ||
      !programId ||
      ![GRE_PROGRAM_ID, TOEFL_PROGRAM_ID].includes(programId) ||
      urlParams.size > 3
    ) {
      setScreenErrorTitile("QR Failed or Expired");
      setScreenErrorMsg(
        "Re-scan the QR code or Refresh the page. If the issue persists contact Admin Team"
      );
    }
    if (contextIdParam) setContextId(contextIdParam);
    if (correlationIdParam) setCorrelationId(correlationIdParam);
    if (programId) setProgramId(programId);
  }, []);

  // #region - Restrict to Mobile
  // Handling the allow only mobile device config
  useEffect(() => {
    const isTheDeviceMobile = hasTouchSupport();
    setIsMobile(isTheDeviceMobile);
    const handleResize = () => {
      setScreenWidth(() => window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [screenWidth]);

  // #region - Error Codes
  const decodeErrorCodes = useCallback(
    (errorMessage) => {
      isErrorFromEventRef.current = true;
      isErrorFromEventRef.current = true;

      switch (errorMessage.IDVError.code) {
        /* docValidationError - This error will be returned when the document validation fails.
      switch (errorMessage.IDVError.code) {
        /* docValidationError - This error will be returned when the document validation fails.
It will include additional data about why it failed. */
        case 113: {
          const docErrMsg = documentErrorCodes(errorMessage.data.code);
          setDocError(docErrMsg);
          break;
        }
        // docEligibilityError - This error will be returned when the document is not eligible.
        case 114:
          setDocError("Document is Not Eligible");
          break;
        case 115:
          setSelfieError(selfieErrorCodes(errorMessage.data[0].errorCode));
          break;
        case 109:
          // docReaderError - This error will be returned when MRZ step fails to start because the license used is invalid.
          // Need to ask the user to reinitialize the journey
          setDocError(
            "Document scan failed. Please try scanning your document again."
          );
          break;
        case 112:
          // documentLockedOut
          setDocError(
            "Too many attempts with the same document without completing the journey."
          );
          break;
        case 108:
          // underAge
          setDocError(
            "When the age is to be checked and doesn't meet the age criteria."
          );
          break;
        case 106:
          // deviceBlocked
          setInitErrorStatus("The device you are using is not allowed.");
          break;
        case 103:
          // nonRecoverable -
          isNextStepsEnabled &&
            setSelfieError(
              "A non-recoverable error occurred. It's recommended to try again later."
            );

          setInitErrorStatus(
            "A non-recoverable error occurred. It's recommended to try again later."
          );
          break;
        case 102:
          // correlationIdError
          setInitErrorStatus(
            "The correlationId provided to start the identity verification journey is invalid"
          );
          break;

        case 101:
          // contextError
          setInitErrorStatus(
            "The clientcontextId provided to start the identity verification journey is invalid."
          );
          break;
        case 100:
          // throttled
          setInitErrorStatus(
            "The system has reached the maximum number of unique simultaneous journeys allowed. Please contact a system administrator for further information."
          );
          break;
        case 118:
          // docReaderError - This error will be returned when MRZ step fails to start because the license used is invalid.
          // Need to ask the user to reinitialize the journey
          setDocError(
            "Captured image is blurry or has glare. Please retake it in a well-lit area without flash, ensuring the text is sharp and in focus"
          );
          break;
        case 105:
          setInitErrorStatus("The system is under maintenance");
          break;

        case 107:
          setInitErrorStatus(
            "An insecure URL (http instead of https) was provided, or if the URL is an invalid URL"
          );
          break;

        default:
          setInitErrorStatus(
            "There's an issue with the system, please try again later"
          );
          break;
      }
    },
    [setDocError, setSelfieError, setInitErrorStatus, isNextStepsEnabled]
  );

  useEffect(() => {
    // #region - Script Load
    // Dynamically load the script as in HTML

    if (startIdvJourney) {
      const script = document.createElement("script");
      script.type = "module";
      script.crossOrigin = "anonymous";
      script.src = process.env.PUBLIC_URL + "/idvaas_ui.js";
      document.body.appendChild(script);

      // Set configuration values when script loads
      script.onload = () => {
        // Set context ID
        const contextInput = document.getElementById("clientContext");
        if (contextInput && contextId) {
          contextInput.value = contextId;
        }

        // Set IDVaaS URL
        const urlInput = document.getElementById("IDVaaSURL");
        if (urlInput) {
          urlInput.value = config.idvaasUrl;
        }

        // Set language
        const languageInput = document.getElementById("language");
        if (languageInput) {
          languageInput.value = config.language;
        }

        // Set correlation ID
        const corrInput = document.getElementById("correlationId");
        if (corrInput && correlationId) {
          corrInput.value = correlationId;
        }
        setIsScriptLoaded(true);
      };

      // Example: Listen for custom events from the library
      const errorHandler = (event) => {
        console.error(event.detail);
        decodeErrorCodes(event.detail);
      };

      document.addEventListener("idvaas-error", errorHandler);

      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
        document.removeEventListener("idvaas-error", errorHandler);
      };
    }
  }, [contextId, correlationId, startIdvJourney, decodeErrorCodes]);

  // Auto-click init button when script is loaded and auto-init is requested
  useEffect(() => {
    if (isScriptLoaded && shouldAutoInit) {
      // Wait a moment for IDVaaS to fully initialize
      setTimeout(() => {
        const initElem = document.getElementById("initbutton");
        if (initElem) {
          initElem.click();
          setShouldAutoInit(false); // Prevent multiple clicks
        } else {
          console.error("Init button not found after script load");
        }
      }, 500);
    }
  }, [isScriptLoaded, shouldAutoInit]);

  // #region - Monitor InitStatus
  //   Monitor initStatus changes

  useEffect(() => {
    if (startIdvJourney) {
      const initStatusElement = document.getElementById("initStatus");
      if (!initStatusElement) return;

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "childList" ||
            mutation.type === "characterData"
          ) {
            const currentStatus =
              initStatusElement.textContent ||
              initStatusElement.innerText ||
              "";
            setInitStatus(currentStatus);

            const isPageLoading = currentStatus
              .toLowerCase()
              .includes("initializing journey");
            setIsLoading(isPageLoading);
            // Check for success conditions
            const isSuccess =
              currentStatus.toLowerCase().includes("initialization complete") ||
              currentStatus
                .toLowerCase()
                .includes("journey initialized successfully");

            // Check for error conditions
            const isError =
              currentStatus.toLowerCase().includes("error") ||
              currentStatus.toLowerCase().includes("failed") ||
              currentStatus.toLowerCase().includes("timeout");
            setIsWorkflowEnabled(isSuccess);
            setIsDocScanEnabled(isSuccess); // Only enable doc scan after init success
            setHasInitError(isError);
          }
        });
      });

      observer.observe(initStatusElement, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      // Initial check
      const currentStatus =
        initStatusElement.textContent || initStatusElement.innerText || "";
      if (currentStatus) {
        setInitStatus(currentStatus);
        const isSuccess =
          currentStatus.toLowerCase().includes("initialization complete") ||
          currentStatus
            .toLowerCase()
            .includes("journey initialized successfully");
        const isError =
          currentStatus.toLowerCase().includes("error") ||
          currentStatus.toLowerCase().includes("failed") ||
          currentStatus.toLowerCase().includes("timeout");
        setIsWorkflowEnabled(isSuccess);
        setIsDocScanEnabled(isSuccess);
        setHasInitError(isError);
      }

      return () => observer.disconnect();
    }
  }, [startIdvJourney]);

  // #region - Monitor selfie status
  // Monitor selfieStatus changes for selfie handling with error code 115 decoding
  useEffect(() => {
    if (startIdvJourney) {
      const selfieStatusElement = document.getElementById("selfieStatus");
      if (!selfieStatusElement) return;

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "childList" ||
            mutation.type === "characterData"
          ) {
            const currentSelfieStatus =
              selfieStatusElement.textContent ||
              selfieStatusElement.innerText ||
              "";
            // Handle selfie success - check for "Selfie Success" specifically
            if (
              currentSelfieStatus === "Selfie Success" ||
              currentSelfieStatus
                .toLowerCase()
                .includes("selfie validation success") ||
              currentSelfieStatus
                .toLowerCase()
                .includes("selfie upload success")
            ) {
              setShowSelfieSuccess(true);
              setSelfieError("");
              setSelfieStatus("");
              setIsLoading(false);
              setJourneySteps((prev) => prev + 1);
              setIsCompleteJourneyEnabled(true);
            }
            // Handle selfie errors including error code 115
            else if (
              currentSelfieStatus.toLowerCase().includes("error") ||
              currentSelfieStatus.toLowerCase().includes("failed") ||
              currentSelfieStatus.toLowerCase().includes("invalid")
            ) {
              setShowSelfieSuccess(false);
              setIsCompleteJourneyEnabled(false);
              // const decodedError = decodeSelfieError(currentSelfieStatus);
              // setSelfieError(decodedError)
              setIsLoading(false);
              setSelfieStatus("");
            }
          }
        });
      });

      observer.observe(selfieStatusElement, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      // Initial check
      const currentSelfieStatus =
        selfieStatusElement.textContent || selfieStatusElement.innerText || "";
      if (currentSelfieStatus) {
        if (
          currentSelfieStatus === "Selfie Success" ||
          currentSelfieStatus
            .toLowerCase()
            .includes("selfie validation success") ||
          currentSelfieStatus.toLowerCase().includes("selfie upload success")
        ) {
          setShowSelfieSuccess(true);
          setSelfieError("");
          setSelfieStatus("");
          setIsCompleteJourneyEnabled(true);
        } else if (
          currentSelfieStatus.toLowerCase().includes("error") ||
          currentSelfieStatus.toLowerCase().includes("failed") ||
          currentSelfieStatus.toLowerCase().includes("invalid")
        ) {
          setShowSelfieSuccess(false);
          setIsCompleteJourneyEnabled(false);
          // const decodedError = decodeSelfieError(currentSelfieStatus);
          // setSelfieError(decodedError);
          setSelfieStatus("");
        }
      }

      return () => observer.disconnect();
    }
  }, [startIdvJourney]);

  //   #region - Monitor doc status
  // Monitor docStatus changes for document scan handling
  useEffect(() => {
    if (startIdvJourney) {
      const docStatusElement = document.getElementById("docStatus");
      if (!docStatusElement) return;

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "childList" ||
            mutation.type === "characterData"
          ) {
            const currentDocStatus =
              docStatusElement.textContent || docStatusElement.innerText || "";
            setDocStatus(currentDocStatus);
            setIsLoading(true);
            setLoadingMessage([
              "Document captured successfully",
              "Processing...",
            ]);
            // Handle document scan success
            if (
              currentDocStatus.toLowerCase().includes("document scan success")
            ) {
              setIsDocScanComplete(true);
              setIsNextStepsEnabled(true);
              setShowDocSuccess(true);
              setDocError("");
              setIsLoading(false);
              setJourneySteps((prev) => prev + 1);
            }
            // Handle document scan errors
            else if (
              currentDocStatus.toLowerCase().includes("error") ||
              currentDocStatus.toLowerCase().includes("failed")
            ) {
              setIsDocScanComplete(false);
              setIsNextStepsEnabled(false);
              setShowDocSuccess(false);
              setIsLoading(false);
              // setHideSelfieBtn(false);
              // Check for specific technical error message and show user-friendly message
              if (
                !isErrorFromEventRef.current &&
                currentDocStatus === "Error, see console logs"
              ) {
                setDocError(
                  "Document scan failed. Please try scanning your document again."
                );
              }
            }
          }
        });
      });

      observer.observe(docStatusElement, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      // Initial check
      const currentDocStatus =
        docStatusElement.textContent || docStatusElement.innerText || "";
      if (currentDocStatus) {
        setDocStatus(currentDocStatus);
        if (currentDocStatus.toLowerCase().includes("document scan success")) {
          setIsDocScanComplete(true);
          setIsNextStepsEnabled(true);
          setShowDocSuccess(true);
          setDocError("");
        } else if (
          currentDocStatus.toLowerCase().includes("error") ||
          currentDocStatus.toLowerCase().includes("failed")
        ) {
          setIsDocScanComplete(false);
          setIsNextStepsEnabled(false);
          setShowDocSuccess(false);
          // Check for specific technical error message and show user-friendly message
          if (
            !isErrorFromEventRef.current &&
            currentDocStatus === "Error, see console logs"
          ) {
            setDocError(
              "Document scan failed. Please try scanning your document again."
            );
          }
        }
      }

      return () => observer.disconnect();
    }
  }, [startIdvJourney]);

  // #region - Monitor completeStatus changes for journey completion handling
  useEffect(() => {
    if (startIdvJourney) {
      const completeStatusElement = document.getElementById("completeStatus");
      if (!completeStatusElement) return;

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "childList" ||
            mutation.type === "characterData"
          ) {
            const currentCompleteStatus =
              completeStatusElement.textContent ||
              completeStatusElement.innerText ||
              "";
            console.log("Complete status changed:", currentCompleteStatus); // Keep for debugging

            // Handle journey completion success
            if (
              currentCompleteStatus === "Journey completed" ||
              currentCompleteStatus
                .toLowerCase()
                .includes("journey completed") ||
              currentCompleteStatus.toLowerCase().includes("journey completed")
            ) {
              setShowJourneyCompleteSuccess(true);
            } else {
              setShowJourneyCompleteSuccess(false);
              if (currentCompleteStatus.toLowerCase().includes("Error")) {
                setScreenErrorTitile("ID-Verification Failed");
                setScreenErrorMsg(
                  "ID-Verification has failed, please retake the verification process"
                );
              }
            }
          }
        });
      });

      observer.observe(completeStatusElement, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      // Initial check
      const currentCompleteStatus =
        completeStatusElement.textContent ||
        completeStatusElement.innerText ||
        "";
      if (currentCompleteStatus) {
        console.log("Initial complete status:", currentCompleteStatus); // Keep for debugging
        if (
          currentCompleteStatus === "Journey completed" ||
          currentCompleteStatus.toLowerCase().includes("journey completed") ||
          currentCompleteStatus.toLowerCase().includes("journey complete")
        ) {
          setShowJourneyCompleteSuccess(true);
        }
      }

      return () => observer.disconnect();
    }
  }, [startIdvJourney]);

  const documentErrorCodes = (code) => {
    switch (code) {
      case -1:
        return "(InvalidMRZ) - Scan document again";
      case -2:
        return "(DocumentExpired) - Must use a valid document";
      case -3:
        return "(DocumentExpiringSoon) - Must use a valid document";
      case -4:
        return "(UnknownMRZValidationError) - Scan the document again";
      case -5:
        return "(CannotApply) Nationality not eligible to continue";
      case -6:
        return "(DocMissingMandatoryFields) - Scan the document again";
      case -9:
        return "(InvalidIssuerOrNationality) – Scan the document again";
      case -10:
        return "(InvalidExpiryDate) - Scan the document again";
      case -11:
        return "(InvalidDateOfBirth) - Document does not include the complete date of birth";
      case -13:
        return "(NotEligible) - Document type not eligible to continue";
      case -14:
        return "(InvalidCheckDigit) - Scan the document again";
      default:
        return "Please rescan the document to continue.";
    }
  };

  const selfieErrorCodes = (code) => {
    switch (code) {
      case 421:
        return "(Face not found) - Face must be in focus from the top of the head to the chin and from ear to ear";
      case 422:
        return "(Multiple faces found) - Only one face should be visible";
      case 423:
        return "(Eyes not found) - Both eyes must be clearly visible";
      case 424:
        return "(Eyes closed) - Eyes should be open";
      case 425:
        return "(Eye gaze not frontal) - Eyes should look into the camera";
      case 426:
        return "(Red eyes)";
      case 427:
        return "(Glasses are tinted) - Glasses allowed when not tinted; no reflection and eyes are not covered";
      case 428:
        return "(Bad exposure) - Adjust the surrounding lighting or your location";
      case 429:
        return "(Non-uniform lighting) - Adjust the surrounding lighting or your location";
      case 430:
        return "(Non-frontal/non-vertical face pose) - Face should face forward and head should be straight";
      case 431:
        return "(Picture is blurry) - Picture is blurry";
      case 432:
        return "(Open mouth) - Mouth should be closed";
      case 433:
        return "(Too close to the camera) - You are too close to the camera, extend your arms to take a photo of you";
      case 434:
        return "(Face not centered horizontally) - Head and shoulders must be visible and centered in the photo";
      case 435:
        return "(Unnatural skin tone) - Show skin tones naturally";
      case 436:
        return "(Hot spots found) - Bright regions resulting from light shining directly on the face";
      case 437:
        return "(Non-uniform background) - The background must be a solid color";
      case 438:
        return "(Width and/or Length of head outside range)";
      case 439:
        return "(Width/Length of head outside range) - Adjust image width to height ratio";
      case 440:
        return "(Bad image width to height ratio) - Take photo with a higher resolution and without compression";
      default:
        return "Photo quality needs improvement. Please retake your selfie";
    }
  };

  const handleRefreshPage = () => {
    window.location.reload();
  };

  // Handle selfie capture from SelfieCapture component
  const handleSelfieCapture = (file) => {
    setSelfieFile(file);
    setSelfieStatus("Selfie captured successfully. Processing...");
    setSelfieError(""); // Clear any previous errors
    setShowSelfieSuccess(false); // Reset success state

    // Integrate with existing IDVaaS selfie upload workflow
    // Create a file input element and trigger the existing upload logic
    const selfieInput = document.getElementById("selfieImage");
    if (selfieInput) {
      // Create a new FileList with our captured file
      const fileList = new DataTransfer();
      fileList.items.add(file);
      selfieInput.files = fileList.files;

      // Trigger change event to integrate with existing IDVaaS handlers
      const event = new Event("change", { bubbles: true });
      selfieInput.dispatchEvent(event);
    }
  };

  // Handle selfie capture errors
  const handleSelfieCaptureError = (error) => {
    console.error("Selfie capture error:", error);
    setShowSelfieSuccess(false);

    // Map camera access errors to user-friendly messages
    let errorMessage =
      "Camera access failed. Please check permissions and try again";
    if (error.message) {
      const message = error.message.toLowerCase();
      if (message.includes("notfound") || message.includes("devicenotfound")) {
        errorMessage =
          "No camera found on this device. Please use a device with a camera";
      } else if (
        message.includes("permission") ||
        message.includes("notallowed")
      ) {
        errorMessage =
          "Camera access required. Please allow camera permissions and try again";
      } else if (message.includes("notsupported")) {
        errorMessage =
          "Camera access not supported in this browser. Please try a different browser";
      }
    }

    setSelfieError(errorMessage);
    setSelfieStatus("");
  };

  const onContinue = () => {
    setStartIdvJourney(true);
    setShouldAutoInit(true); // Flag to auto-click init once script loads
    setIsLoading(true);
    toNextPage();
  };

  const toNextPage = () => {
    setJourneySteps((prev) => prev + 1);
  };

  const hasTouchSupport = () => {
    return "ontouchstart" in window || navigator.maxTouchPoints > 0;
  };

  // const handleHideSelfieBtn = () => {
  //   console.log("handleHide Selfie Btn called", hideSelfieBtn);
  //   isErrorFromEventRef.current = false;
  //   setHideSelfieBtn(true);
  // };

  // #region - Render HTML Code
  if (screenWidth >= 720 || !isMobile || screenErrorMsg.length) {
    const renderMsg =
      "This application is optimized for mobile devices. Please use your phone to continue.";
    const renderTitle = "Mobile Only";
    return (
      <ScreenSizeError
        errorMsg={screenErrorMsg || renderMsg}
        errorTitle={screenErrorTitle || renderTitle}
      />
    );
  }
  return (
    <div>
      {journeySteps == 1 && (
        <div>
          <LandingScreen onContinue={toNextPage} programId={programId} />
        </div>
      )}

      {journeySteps == 2 && <TermsAndConditions toNextPage={toNextPage} />}

      {journeySteps == 3 && <Consent toNextPage={toNextPage} />}

      {journeySteps == 4 && <StartJourney onContinue={onContinue} />}

      {journeySteps == 9 && <CompleteJourneyScreenNew />}

      {startIdvJourney && (
        <div id="container">
          {/* IDV Logo Title */}
          {isLoading && <LoadingSpinner loadingMessage={loadingMessage} />}
          {/* Revert if failed - Logo Header */}
          {/* <div className="logo-header">
            <IDVLogo />
          </div> */}
          {/* Hidden inputs for IDVaaS library */}
          <input id="clientContext" type="hidden" />
          <input id="IDVaaSURL" type="hidden" />
          <input id="language" type="hidden" />
          <input id="correlationId" type="hidden" />

          <div className="step-container">
            <button
              id="initbutton"
              className="workflow-btn"
              aria-label="Initialize Journey - Step 1"
              style={{ display: "none" }}
            >
              1. Initialize Journey
            </button>
            <p
              id="initStatus"
              ref={initStatusRef}
              style={{
                display: "none",
              }}
            ></p>
          </div>
          {/* <hr /> */}
          <div
            style={{
              display: `${journeySteps == 5 ? "block" : "none"}`,
            }}
          >
            <ScanDocumentScreenNew
              showDocSuccess={showDocSuccess}
              docError={docError}
              hasInitError={hasInitError}
              isDocScanEnabled={isDocScanEnabled}
              initStatus={initStatus}
              initErrorStatus={initErrorStatus}
              docStatus={docStatus}
              setDocError={setDocError}
              setDocStatus={setDocStatus}
            />
          </div>
          <div className="step-container">
            {/* Hidden file input for IDVaaS integration */}
            <input
              type="file"
              id="selfieImage"
              accept="image/jpeg"
              disabled={!isNextStepsEnabled}
              style={{ display: "none" }}
              aria-hidden="true"
            />

            <div
              style={{
                display: `${journeySteps == 6 ? "block" : "none"}`,
              }}
            >
              <SelfieScreenOnce toNextPage={toNextPage} />
            </div>

            {/* Selfie Capture Component */}
            <div
              style={{
                display: `${journeySteps == 7 ? "block" : "none"}`,
              }}
            >
              <SelfieScreenTwo
                onCapture={handleSelfieCapture}
                onError={handleSelfieCaptureError}
                showSelfieSuccess={showSelfieSuccess}
                selfieError={selfieError}
                selfieStatus={selfieStatus}
                isNextStepsEnabled={isNextStepsEnabled}
                setSelfieError={setSelfieError}
                setIsLoading={setIsLoading}
                setLoadingMessage={setLoadingMessage}
              />
            </div>

            {/* Status display - show both our status and IDVaaS status */}
          </div>

          <div className="step-container">
            <div style={{ display: `${journeySteps == 8 ? "block" : "none"}` }}>
              {/* TODO: revert after testing and add another journey step */}
              <ProcessCompleteJourney
                showJourneyCompleteSuccess={showJourneyCompleteSuccess}
                isCompleteJourneyEnabled={isCompleteJourneyEnabled}
                toNextPage={toNextPage}
                journeySteps={journeySteps}
              />
            </div>
          </div>
          <div className="overlay remove" id="container-elems"></div>
        </div>
      )}
    </div>
  );
}

export default App;
